import type { SimCareerView, SimChoiceResponse, SimEventView, SimMe } from '@cruz/shared';
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { navigate } from '../challenge/router.ts';
import { CrossMark } from '../components/icons.tsx';
import { load, save } from '../lib/storage.ts';
import { simApi, simErrorMessage } from './api.ts';
import { countdown, daysUntil, num, plural } from './format.ts';
import { CastRow, Portrait } from './Cast.tsx';
import { AchievementCard, Delta, signedEuros } from './Changes.tsx';
import { ErrandLine, ErrandNotes, ErrandsCard } from './Errands.tsx';
import { NextStepCard } from './NextStep.tsx';
import { ReportButton } from './Report.tsx';
import { ShareButton } from './Share.tsx';
import { TramoCard, TramoGame } from './Tramo.tsx';
import { card, Notice, primaryButton, secondaryButton, Working } from './ui.tsx';

/** "Hoy": the event of the moment, what happened after choosing, and the end of the day. */

type Step =
  | { step: 'loading' }
  | { step: 'event'; event: SimEventView; choosing: number | null }
  | {
      step: 'outcome';
      event: SimEventView;
      choice: number;
      result: SimChoiceResponse;
      before: SimCareerView;
    }
  | { step: 'done' }
  /** The minigame: offered between events (`between`) or from the end of the day */
  | { step: 'tramo'; between: boolean }
  | { step: 'error'; message: string };

const hookKey = (careerId: string) => `cruz.sim.hook.${careerId}`;

/** Re-renders every second while mounted. */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

export function TodayTab({
  me,
  career,
  onCareer,
  onNewCareer,
}: {
  me: SimMe;
  career: SimCareerView;
  onCareer: (c: SimCareerView) => void;
  onNewCareer: () => void;
}) {
  const [step, setStep] = useState<Step>({ step: 'loading' });
  const fetchEvent = useCallback(
    () =>
      simApi
        .event()
        .then((r) => {
          onCareer(r.career);
          setStep(r.event ? { step: 'event', event: r.event, choosing: null } : { step: 'done' });
        })
        .catch((err: unknown) => setStep({ step: 'error', message: simErrorMessage(err) })),
    [onCareer],
  );
  useEffect(() => {
    void fetchEvent();
  }, [fetchEvent]);
  const loadEvent = () => {
    setStep({ step: 'loading' });
    void fetchEvent();
  };

  const choose = async (event: SimEventView, choice: number) => {
    setStep({ step: 'event', event, choosing: choice });
    const before = career;
    try {
      const result = await simApi.choose(event.idx, choice);
      if (result.hook) save(hookKey(before.id), { day: before.today.day, text: result.hook });
      onCareer(result.career);
      setStep({ step: 'outcome', event, choice, result, before });
      scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setStep({ step: 'error', message: simErrorMessage(err) });
    }
  };

  return (
    <div className="space-y-4">
      {!me.guest && !me.email_verified && <VerifyReminder />}
      {step.step === 'loading' && <Working label="Viendo qué pasa hoy en tu hermandad…" />}
      {step.step === 'error' && (
        <div className="space-y-3">
          <Notice tone="error">{step.message}</Notice>
          <button type="button" className={secondaryButton} onClick={loadEvent}>
            Volver a probar
          </button>
        </div>
      )}
      {step.step === 'event' && (
        <EventCard
          event={step.event}
          career={career}
          choosing={step.choosing}
          onChoose={(i) => void choose(step.event, i)}
        />
      )}
      {step.step === 'outcome' && (
        <Outcome
          event={step.event}
          choice={step.choice}
          result={step.result}
          before={step.before}
          onNext={() => {
            const c = step.result.career;
            if (step.result.day_done) setStep({ step: 'done' });
            // Halfway through the day, a break from reading
            else if (
              c.today.tramo?.available &&
              c.today.events_left === c.today.events_per_day - 2
            ) {
              setStep({ step: 'tramo', between: true });
              scrollTo({ top: 0 });
            } else loadEvent();
          }}
        />
      )}
      {step.step === 'tramo' && (
        <TramoGame
          onCareer={onCareer}
          onDone={() => {
            scrollTo({ top: 0 });
            if (step.between) loadEvent();
            else setStep({ step: 'done' });
          }}
          onSkip={step.between ? loadEvent : undefined}
        />
      )}
      {step.step === 'done' &&
        (career.finished ? (
          <Finished career={career} onNewCareer={onNewCareer} />
        ) : (
          <DayDone
            career={career}
            guest={me.guest}
            onNewDay={loadEvent}
            onTramo={() => {
              setStep({ step: 'tramo', between: false });
              scrollTo({ top: 0 });
            }}
          />
        ))}
    </div>
  );
}

function VerifyReminder() {
  const [state, setState] = useState<'idle' | 'sent' | 'error'>('idle');
  return (
    <Notice tone="warn">
      <p>Verifica tu correo con el enlace que te mandamos: así no pierdes tu cuenta.</p>
      {state === 'idle' && (
        <button
          type="button"
          className="mt-1 font-semibold text-purple underline decoration-gold underline-offset-4 dark:text-gold-300"
          onClick={() => {
            simApi
              .resendVerification()
              .then(() => setState('sent'))
              .catch(() => setState('error'));
          }}
        >
          Mandármelo otra vez
        </button>
      )}
      {state === 'sent' && <p className="mt-1 font-medium">Enviado. Mira tu correo (y el spam).</p>}
      {state === 'error' && (
        <p className="mt-1 font-medium">Espera un par de minutos y vuelve a probar.</p>
      )}
    </Notice>
  );
}

function DayProgress({ career }: { career: SimCareerView }) {
  const { events_per_day: total, events_left: left } = career.today;
  const current = Math.min(total, total - left + 1);
  return (
    <div className="flex items-center gap-3 text-sm text-ink-soft dark:text-cream/75">
      <span className="flex gap-1.5" aria-hidden>
        {Array.from({ length: total }, (_, i) => (
          <span
            key={i}
            className={`h-2 w-6 rounded-full ${i < total - left ? 'bg-gold' : i === total - left ? 'bg-purple dark:bg-gold-300' : 'bg-purple/15 dark:bg-cream/15'}`}
          />
        ))}
      </span>
      <span>
        Hoy, {current} de {total} · {career.time.phase_label} · año {career.time.year}
      </span>
    </div>
  );
}

function EventCard({
  event,
  career,
  choosing,
  onChoose,
}: {
  event: SimEventView;
  career: SimCareerView;
  choosing: number | null;
  onChoose: (i: number) => void;
}) {
  // A new event: screen readers start from its title
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => titleRef.current?.focus({ preventScroll: true }), [event.idx]);
  return (
    <article aria-labelledby="event-title" className="space-y-4">
      <DayProgress career={career} />
      <ErrandLine errands={career.today.errands} cast={event.cast.map((c) => c.id)} />
      {/* One line of context at most: what comes back from days ago, or yesterday's hook */}
      {(event.callback ?? event.from_yesterday) && (
        <p
          className={`rounded-xl border-l-4 bg-cream-200 px-4 py-2 text-[0.95rem] italic ${event.callback ? 'border-burgundy dark:border-burgundy-300 dark:bg-burgundy/20' : 'border-gold dark:bg-gold/10'}`}
        >
          {event.callback ?? `Ayer: ${event.from_yesterday}`}
        </p>
      )}
      <div className={`${card} px-5 py-5`}>
        <h1
          id="event-title"
          ref={titleRef}
          tabIndex={-1}
          className="outline-none font-serif text-[1.9rem] leading-tight font-semibold text-purple dark:text-gold-300"
        >
          {event.title}
        </h1>
        <CastRow cast={event.cast} />
        <p className="mt-3 text-[1.08rem] leading-relaxed whitespace-pre-line">{event.text}</p>
      </div>
      <h2 id="event-options" className="sr-only">
        ¿Qué haces?
      </h2>
      <ul aria-labelledby="event-options" className="space-y-3">
        {event.options.map((o) => (
          <li key={o.index}>
            <button
              type="button"
              disabled={choosing !== null}
              onClick={() => onChoose(o.index)}
              className={`flex min-h-14 w-full items-start gap-3 rounded-xl border px-4 py-3 text-left text-[1.05rem] leading-snug transition duration-150 active:scale-[0.99] disabled:cursor-default ${
                choosing === o.index
                  ? 'border-gold bg-cream-200 dark:bg-gold/15'
                  : 'border-purple/20 bg-paper hover:border-gold dark:border-cream/20 dark:bg-night-800 dark:hover:border-gold'
              } ${choosing !== null && choosing !== o.index ? 'opacity-50' : ''}`}
            >
              <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-purple font-serif text-sm font-semibold text-cream dark:bg-gold-300 dark:text-night">
                {o.index + 1}
              </span>
              <span>{o.text}</span>
            </button>
          </li>
        ))}
      </ul>
      {choosing !== null ? (
        <Working label="A ver qué pasa…" />
      ) : (
        <ReportButton
          what="este evento"
          onSend={(reason, comment) => simApi.reportEvent(event.idx, reason, comment)}
        />
      )}
    </article>
  );
}

const BookmarkIcon = () => (
  <svg
    width={18}
    height={18}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M6 3h12v18l-6-4-6 4z" />
  </svg>
);

/** "Se acordará de esto": what this choice left with someone, or how a story ended. */
function MemoryNote({ kind, text }: { kind: string; text: string }) {
  const warm = kind === 'favor' || kind === 'collected' || kind === 'kept' || kind === 'mended';
  return (
    <li
      className={`animate-rise flex items-start gap-2.5 rounded-xl px-3.5 py-2.5 text-[0.98rem] font-medium ${
        warm
          ? 'bg-gold/15 text-gold-700 dark:bg-gold/10 dark:text-gold-300'
          : 'bg-burgundy-300/25 text-burgundy dark:bg-burgundy/25 dark:text-burgundy-300'
      }`}
    >
      <span className="mt-0.5 flex-none">
        <BookmarkIcon />
      </span>
      {text}
    </li>
  );
}

/** Favors and grudges still open: what will come back. */
function OpenStories({ career }: { career: SimCareerView }) {
  const open = career.characters.filter((c) => c.thread);
  if (!open.length) return null;
  return (
    <section aria-labelledby="open-stories" className={`${card} px-5 py-4`}>
      <h2 id="open-stories" className="font-serif text-xl font-semibold">
        Historias abiertas
      </h2>
      <ul className="mt-3 space-y-2.5">
        {open.map((c) => (
          <li key={c.id} className="flex items-center gap-3">
            <Portrait id={c.id} name={c.name} src={c.portrait} size={44} />
            <p className="text-[0.98rem]">
              {c.thread === 'favor' ? (
                <>
                  <strong>{c.name.split(',')[0]}</strong> te debe una.
                </>
              ) : (
                <>
                  Lo de <strong>{c.name.split(',')[0]}</strong> sigue ahí.
                </>
              )}
            </p>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-sm text-ink-soft dark:text-cream/70">
        Volverán a salir cuando menos te lo esperes.
      </p>
    </section>
  );
}

/** Rose petals over a promotion, like the ones thrown at the paso. Decorative only. */
function Petals() {
  // Fixed positions: the same shower every time, no randomness in render
  const petals = [
    [8, 0, 18, 260],
    [22, 350, -14, -220],
    [35, 120, 24, 300],
    [48, 600, -20, 280],
    [61, 220, 16, -320],
    [74, 480, -24, 240],
    [86, 80, 12, 340],
    [15, 820, 20, -260],
    [55, 950, -16, 300],
    [92, 700, -10, -280],
  ] as const;
  return (
    <span className="pointer-events-none absolute inset-0" aria-hidden>
      {petals.map(([left, delay, drift, spin], i) => (
        <span
          key={i}
          className={`animate-petal absolute top-0 h-2.5 w-2 rounded-[60%_10%_60%_10%] ${
            i % 3 === 0 ? 'bg-gold-300' : i % 3 === 1 ? 'bg-burgundy-300' : 'bg-cream/90'
          }`}
          style={
            {
              left: `${left}%`,
              animationDelay: `${delay}ms`,
              '--drift': `${drift}px`,
              '--spin': `${spin}deg`,
            } as CSSProperties
          }
        />
      ))}
    </span>
  );
}

function Outcome({
  event,
  choice,
  result,
  before,
  onNext,
}: {
  event: SimEventView;
  choice: number;
  result: SimChoiceResponse;
  before: SimCareerView;
  onNext: () => void;
}) {
  const c = result.changes;
  const b = c.brotherhood;
  const moments = result.career.timeline.slice(before.timeline.length).filter((t) => t.shareable);
  const promoted = Boolean(c.promoted);
  const juicy = Boolean(
    c.promoted ?? c.thread_end ?? c.memory ?? event.callback ?? event.last_of_day,
  );
  // The options are gone: screen readers go on from what happened
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => titleRef.current?.focus({ preventScroll: true }), []);
  // A promotion is felt in the hand too (phones that can vibrate)
  useEffect(() => {
    if (
      promoted &&
      typeof matchMedia === 'function' &&
      !matchMedia('(prefers-reduced-motion: reduce)').matches
    )
      navigator.vibrate?.([40, 60, 40]);
  }, [promoted]);
  return (
    <article aria-labelledby="outcome-title" className="space-y-4">
      {/* The big news first, in sight without scrolling */}
      {c.promoted && (
        <div
          role="status"
          className="animate-crown relative overflow-hidden rounded-2xl border-2 border-gold bg-purple px-5 py-6 text-center text-cream dark:bg-night-700"
        >
          <Petals />
          <span className="relative mx-auto block w-fit text-gold-300">
            <CrossMark size={30} />
          </span>
          <p className="relative mt-2 text-xs font-semibold tracking-[0.2em] text-gold-300 uppercase">
            ¡Ascenso!
          </p>
          <p className="relative mt-1 font-serif text-[2rem] leading-tight font-semibold">
            {c.promoted.rank_label}
          </p>
          {c.promoted.post && (
            <p className="relative mt-1 text-lg text-cream/85">{c.promoted.post}</p>
          )}
        </div>
      )}
      <div className={`${card} px-5 py-5`}>
        <p className="text-sm text-ink-soft dark:text-cream/70">{event.title}</p>
        {/* The chosen option, as it looked, and what happened right below it */}
        <p className="mt-2 flex items-start gap-3 rounded-xl border border-gold bg-cream-200 px-3 py-2.5 leading-snug dark:bg-gold/15">
          <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-purple font-serif text-sm font-semibold text-cream dark:bg-gold-300 dark:text-night">
            {choice + 1}
          </span>
          <span className="sr-only">Elegiste:</span>
          <span>{event.options[choice]?.text}</span>
        </p>
        <h1 id="outcome-title" ref={titleRef} tabIndex={-1} className="sr-only outline-none">
          Lo que pasó
        </h1>
        <p className="mt-3 font-serif text-[1.35rem] leading-snug whitespace-pre-line">
          {result.outcome}
        </p>
        {(c.stats.length > 0 || c.relation || b.members || b.prestige || b.treasury) && (
          <ul aria-label="Cambios" className="mt-4 flex flex-wrap gap-2">
            {c.stats.map((s) => (
              <Delta key={s.key} label={s.label} delta={s.delta} />
            ))}
            {c.relation && (
              <Delta label={`Relación con ${c.relation.name}`} delta={c.relation.delta} />
            )}
            {b.members !== 0 && <Delta label="Hermanos" delta={b.members} />}
            {b.prestige !== 0 && <Delta label="Prestigio de la hermandad" delta={b.prestige} />}
            {b.treasury !== 0 && <Delta label="Caja" delta={b.treasury} format={signedEuros} />}
          </ul>
        )}
        {(Boolean(c.memory ?? c.thread_end) || c.errands.length > 0) && (
          <ul aria-label="Lo que queda" className="mt-4 space-y-2">
            <ErrandNotes errands={c.errands} />
            {c.thread_end && <MemoryNote kind={c.thread_end.end} text={c.thread_end.text} />}
            {c.memory && <MemoryNote kind={c.memory.kind} text={c.memory.text} />}
          </ul>
        )}
      </div>

      {/* A question for friends, only on the decisions with something to talk about */}
      {juicy && (
        <div className={`${card} flex flex-wrap items-center justify-between gap-3 px-4 py-3`}>
          <p className="text-[0.98rem]">¿Qué habrían hecho tus amigos?</p>
          <ShareButton request={{ kind: 'decision', ref: String(event.idx) }} label="Pregúntales" />
        </div>
      )}
      {c.year_ended && (
        <Notice>Se cierra el año cofrade. Sumas un año de antigüedad y la nómina se mueve.</Notice>
      )}
      {moments.map((m) => (
        <div
          key={m.index}
          className={`${card} flex flex-wrap items-center justify-between gap-3 px-4 py-3`}
        >
          <p className="font-medium">{m.text}</p>
          <ShareButton request={{ kind: 'moment', ref: String(m.index) }} />
        </div>
      ))}
      {c.achievements.map((a) => (
        <AchievementCard key={a.id} achievement={a} />
      ))}

      {result.hook && (
        <div className="rounded-2xl border-l-4 border-burgundy bg-paper px-5 py-4 dark:border-burgundy-300 dark:bg-night-800">
          <p className="text-xs font-semibold tracking-[0.16em] text-burgundy uppercase dark:text-burgundy-300">
            Mañana…
          </p>
          <p className="mt-1 text-lg italic">{result.hook}</p>
        </div>
      )}
      <button type="button" className={primaryButton} onClick={onNext}>
        {result.day_done ? 'Terminar el día' : 'Siguiente'}
      </button>
      <ReportButton
        what="este evento"
        onSend={(reason, comment) => simApi.reportEvent(event.idx, reason, comment)}
      />
    </article>
  );
}

/** How far today's choices moved the numbers. */
function DayNumbers({ career }: { career: SimCareerView }) {
  const { stats, brotherhood: b } = career.today.progress;
  const moved = stats.filter((x) => x.delta !== 0);
  const any = moved.length > 0 || b.members !== 0 || b.prestige !== 0 || b.treasury !== 0;
  return (
    <section aria-labelledby="day-progress" className={`${card} px-5 py-4`}>
      <h2 id="day-progress" className="font-serif text-xl font-semibold">
        Tu día en números
      </h2>
      {any ? (
        <ul aria-label="Cambios de hoy" className="mt-3 flex flex-wrap gap-2">
          {moved.map((x) => (
            <Delta key={x.key} label={x.label} delta={x.delta} />
          ))}
          {b.members !== 0 && <Delta label="Hermanos" delta={b.members} />}
          {b.prestige !== 0 && <Delta label="Prestigio de la hermandad" delta={b.prestige} />}
          {b.treasury !== 0 && <Delta label="Caja" delta={b.treasury} format={signedEuros} />}
        </ul>
      ) : (
        <p className="mt-2 text-ink-soft dark:text-cream/75">Hoy tus números no se han movido.</p>
      )}
    </section>
  );
}

/** A guest's day is over: the moment to keep the career (never a wall: they can go on). */
function SaveCareer() {
  return (
    <section
      aria-labelledby="save-career"
      className="rounded-2xl border-2 border-gold bg-cream-200 px-5 py-5 dark:bg-gold/10"
    >
      <h2 id="save-career" className="font-serif text-2xl font-semibold">
        Guarda tu carrera
      </h2>
      <p className="mt-2 text-[1.02rem]">
        Tu hermandad, tu gente y lo que dejaste a medias solo viven en este móvil. Guárdala con una
        cuenta y mañana sigues donde lo dejaste, desde cualquier sitio.
      </p>
      <a
        href="/simulador/registro"
        onClick={(e) => {
          e.preventDefault();
          navigate('/simulador/registro');
        }}
        className={`${primaryButton} mt-4`}
      >
        Guardar mi carrera
      </a>
      <p className="mt-2 text-center text-sm text-ink-soft dark:text-cream/70">
        Gratis. Solo un apodo, tu correo y una contraseña.
      </p>
    </section>
  );
}

function DayDone({
  career,
  guest,
  onNewDay,
  onTramo,
}: {
  career: SimCareerView;
  guest: boolean;
  onNewDay: () => void;
  onTramo: () => void;
}) {
  const now = useNow();
  const hook = load<{ day: string; text: string } | null>(hookKey(career.id), null);
  const next = career.today.next_day_at;
  const left = next ? new Date(next).getTime() - now : 0;
  const hw = career.today.holy_week;
  const days = daysUntil(hw.starts_at, now);
  const t = career.time;
  return (
    <div className="space-y-4">
      <div className={`${card} px-5 py-5 text-center`}>
        <h1 className="text-xs font-semibold tracking-[0.16em] text-gold-700 uppercase dark:text-gold-300">
          Por hoy, esto es todo
        </h1>
        {hook && hook.day === career.today.day ? (
          <p className="mt-3 font-serif text-2xl leading-snug italic">{hook.text}</p>
        ) : (
          <p className="mt-3 font-serif text-2xl leading-snug">
            Tu hermandad sigue su vida. Mañana, más.
          </p>
        )}
        <div className="mt-5 space-y-1 border-t border-gold/40 pt-4">
          {left > 0 ? (
            <p className="text-lg">
              Mañana a las 6:00 sigue tu historia: quedan{' '}
              <strong className="tabular">{countdown(left)}</strong>.
            </p>
          ) : (
            <button type="button" className={primaryButton} onClick={onNewDay}>
              Empezar el día
            </button>
          )}
          <p className="text-sm text-ink-soft dark:text-cream/70">
            Racha: {plural(t.streak, 'día', 'días')}
            {t.streak_saved && ' (ayer te la guardamos)'} · Semana Santa:{' '}
            {hw.on_now ? '¡ya está aquí!' : days === 1 ? 'mañana' : `faltan ${num(days)} días`}
          </p>
        </div>
      </div>
      {career.today.tramo?.available && <TramoCard tramo={career.today.tramo} onPlay={onTramo} />}
      {guest && <SaveCareer />}
      {/* The rest, for whoever wants it: the day ends without more reading */}
      <details className={`${card} group px-5 py-1`}>
        <summary className="flex min-h-12 cursor-pointer list-none items-center justify-between font-semibold text-purple dark:text-gold-300 [&::-webkit-details-marker]:hidden">
          Ver más: tus números, encargos e historias
          <span aria-hidden className="transition-transform group-open:rotate-90">
            ›
          </span>
        </summary>
        <div className="space-y-4 pt-2 pb-4">
          <DayNumbers career={career} />
          {career.today.tramo && !career.today.tramo.available && (
            <TramoCard tramo={career.today.tramo} onPlay={onTramo} />
          )}
          <ErrandsCard errands={career.today.errands} dayOver={career.today.events_left === 0} />
          <OpenStories career={career} />
          {career.player.next_step && <NextStepCard step={career.player.next_step} />}
          <p className="text-sm text-ink-soft dark:text-cream/70">
            Mejor racha: {plural(t.best_streak, 'día', 'días')}. Un día de descanso a la semana no
            la rompe.
          </p>
          <a href="/reto/nuevo" className={secondaryButton}>
            Reta a tus amigos en el reto cofrade
          </a>
        </div>
      </details>
    </div>
  );
}

function Finished({ career, onNewCareer }: { career: SimCareerView; onNewCareer: () => void }) {
  const end = [...career.timeline].reverse().find((t) => t.kind === 'end' && t.shareable);
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border-2 border-gold bg-purple px-5 py-6 text-center text-cream dark:bg-night-700">
        <p className="text-xs font-semibold tracking-[0.16em] text-gold-300 uppercase">
          Fin de tu carrera
        </p>
        <p className="mt-2 font-serif text-3xl font-semibold">{career.player.rank_label}</p>
        <p className="mt-2">
          {plural(career.player.seniority_years, 'año', 'años')} de hermano ·{' '}
          {plural(career.player.processions, 'estación de penitencia', 'estaciones de penitencia')}
        </p>
        {end && (
          <div className="mt-4 flex justify-center">
            <ShareButton
              request={{ kind: 'moment', ref: String(end.index) }}
              label="Compartir mi carrera"
            />
          </div>
        )}
      </div>
      <button type="button" className={primaryButton} onClick={onNewCareer}>
        Empezar otra carrera
      </button>
    </div>
  );
}
